import { BreakpointObserver, } from '@angular/cdk/layout';
import { isDevMode, OnDestroy, OnInit, } from '@angular/core';
import { MatSidenav, MatSidenavContainer, } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { AppStateFacade, Setting, } from '@app-state';
import { PageMetadataRegistry, } from '@app/router';
import { FOLDER_ID_PARAM, } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { ExpiredModalComponent } from '@pages/license/modals/expired/expired.modal';
import { AlertsService, AuthService, LanguageService, ModalService, NotificationSeverity, SignalRService, StorageService, } from '@services';
import { SnackBarType, UiSnackBarService, } from '@uipath/angular/components/ui-snackbar';
import { filterNullOrUndefined } from '@utils/rxjs.utils';
import { forkJoin, Subject, } from 'rxjs';
import { distinctUntilChanged, switchMap, take, takeUntil, throttleTime, } from 'rxjs/operators';
import { InactivityTimer } from './inactivity-timer';
var BREAKPOINT = '(min-width: 960px)';
var SEVERITY_SNACKBAR_MAP = new Map([
    [NotificationSeverity.Info, SnackBarType.Info],
    [NotificationSeverity.Success, SnackBarType.Success],
    [NotificationSeverity.Warn, SnackBarType.Warning],
    [NotificationSeverity.Error, SnackBarType.Error],
    [NotificationSeverity.Fatal, SnackBarType.Error],
]);
var UiPathComponent = /** @class */ (function () {
    function UiPathComponent(_inactivityTimer, translateService, _appState, _router, _signalRService, _modalService, _auth, _breakpointObserver, _storageService, _alertsService, _languageService, _snack, _pageRegistry) {
        var _this = this;
        this.translateService = translateService;
        this._appState = _appState;
        this._router = _router;
        this._signalRService = _signalRService;
        this._modalService = _modalService;
        this._auth = _auth;
        this._breakpointObserver = _breakpointObserver;
        this._storageService = _storageService;
        this._alertsService = _alertsService;
        this._languageService = _languageService;
        this._snack = _snack;
        this._pageRegistry = _pageRegistry;
        this.showAccessDeniedTitle = false;
        this.sideNavOpened = true;
        this._defaultTenantName = 'Default';
        this._destroyed$ = new Subject();
        this.updateSidebarWidth = function (width) {
            _this._sidebarWidth = width;
            _this.sidenavContainer.updateContentMargins();
        };
        this._onNotificationReceived = function (notification) {
            if (notification.severity === NotificationSeverity.Error ||
                notification.severity === NotificationSeverity.Fatal ||
                (notification.data && notification.data.forceDisplay)) {
                _this._showNotification(notification);
                _this._alertsService.incrementCount();
            }
        };
        this.authConfig$ = this._appState.authConfig.value$;
        this._appState.settings.get$(Setting.customTitle)
            .pipe(takeUntil(this._destroyed$))
            .subscribe(function (updatedCustomTitle) { return _this.customTitle = updatedCustomTitle; });
        this.uiPathLogoRoute = '/';
        this._signalRService.license$
            .pipe(switchMap(function () { return forkJoin([
            _this._appState.user.license.fetch(),
            _this._appState.settings.fetch(),
        ]); }), takeUntil(this._destroyed$)).subscribe();
        this._appState.user.folder.value$
            .pipe(takeUntil(this._destroyed$)).subscribe(function () { return _this.hasFolder = _this._appState.user.folder.hasAny; });
        this.license$ = this._appState.user.license.value$;
    }
    Object.defineProperty(UiPathComponent.prototype, "sidebarWidth", {
        get: function () {
            return this.sidenav.mode === 'side' ?
                this._sidebarWidth :
                0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UiPathComponent.prototype, "helpUrl", {
        get: function () {
            return this._appState.settings.get(Setting.helpUrl);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UiPathComponent.prototype, "isDevMode", {
        get: function () {
            return isDevMode();
        },
        enumerable: true,
        configurable: true
    });
    UiPathComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._preventClickJacking();
        this.currentUser$ = this._appState.user.value$;
        this.isHost = this._appState.user.loggedInAsHost;
        if (!this.isHost) {
            this._appState.user.folder.value$
                .pipe(filterNullOrUndefined(), takeUntil(this._destroyed$))
                .subscribe(function (folder) {
                var _a;
                var paramMap = _this._router.routerState.snapshot.root.queryParamMap;
                if (Number(paramMap.get(FOLDER_ID_PARAM)) === folder.id ||
                    !_this._appState.user.folder.hasAny) {
                    return;
                }
                _this._router.navigate([], {
                    queryParams: (_a = {},
                        _a[FOLDER_ID_PARAM] = folder.id,
                        _a),
                    preserveFragment: true,
                    queryParamsHandling: 'merge',
                });
            });
        }
        this._storageService
            .watch('loggedInUser')
            .pipe(takeUntil(this._destroyed$))
            .subscribe(function (event) {
            if (event.newValue === 'null') {
                _this._modalService.closeAllDialogs();
                _this.logout();
                _this._modalService.onAlert(_this.translateService.instant('main_logged_out_message'));
            }
            else if (event.newValue !== _this._appState.user.value.id.toString()) {
                // the user has changed, reload the page
                window.location.reload();
            }
        });
        this._signalRService.connect();
        this._appState.language.change$
            .pipe(takeUntil(this._destroyed$)).subscribe(function () { return _this._signalRService.monitoringSubscribe(); });
        this._appState.user.license.value$
            .pipe(take(1), takeUntil(this._destroyed$))
            .subscribe(function (license) {
            if (!!license &&
                license.isRegistered &&
                !license.isExpired &&
                license.expirationDate < new Date()) {
                _this._modalService.openModal(ExpiredModalComponent, license);
            }
        });
        this._signalRService.notification$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(this._onNotificationReceived);
        this._signalRService.watchPermissions();
        this._signalRService.watchAllLicenses();
        this.uiPathLogoRoute = this._appState.user.authState === 1 /* LoggedInAsTenant */ ? '/' : '/tenants';
        this.menuItems = [
            {
                type: "userProfile" /* userProfile */,
            }
        ].concat(this._pageRegistry.userMenuPages.map(function (p) { return ({
            page: p,
            type: "page" /* page */,
        }); }), [
            {
                type: "logout" /* logout */,
            },
        ]);
        this.languages$ = this._languageService.getAll();
        if (this._breakpointObserver.isMatched(BREAKPOINT)) {
            this.sidenav.mode = 'side';
        }
        else {
            this.sideNavOpened = false;
            this.sidenav.mode = 'over';
        }
        this._watchScreen();
    };
    UiPathComponent.prototype.ngOnDestroy = function () {
        this._destroyed$.next();
        this._signalRService.disconnect();
    };
    UiPathComponent.prototype.getUserName = function (currentUser) {
        var displayName = currentUser.fullName.trim() ? currentUser.fullName : currentUser.userName;
        if (currentUser.tenantDisplayName &&
            currentUser.tenantDisplayName !== this._defaultTenantName) {
            return currentUser.tenantDisplayName + "/" + displayName;
        }
        return displayName;
    };
    UiPathComponent.prototype.getInitials = function (currentUser) {
        if (!currentUser.fullName.trim()) {
            return currentUser.userName[0];
        }
        return "" + (currentUser.name[0] || '') + (currentUser.surname[0] || '');
    };
    UiPathComponent.prototype.toggleSidebar = function () {
        this.sidenav.toggle();
    };
    UiPathComponent.prototype.logout = function () {
        this._signalRService.disconnect();
        this._auth.logout();
    };
    UiPathComponent.prototype.changeLanguage = function (language) {
        forkJoin([
            this._appState.language
                .set(language),
            this._appState.user
                .saveLanguage(language),
        ]).subscribe();
    };
    UiPathComponent.prototype.onOpenNotificationSettings = function () {
        this._router.navigate(['/profile']);
    };
    UiPathComponent.prototype._watchScreen = function () {
        var _this = this;
        this._breakpointObserver.observe(BREAKPOINT)
            .pipe(distinctUntilChanged(), 
        // cap to 60 events per second
        throttleTime(1000 / 60), takeUntil(this._destroyed$))
            .subscribe(function (state) {
            var matches = state.matches;
            _this.sidenav.mode = matches ? 'side' : 'over';
            if (matches) {
                _this.sidenav.open();
            }
            else if (matches === false) {
                _this.sidenav.close();
            }
        });
    };
    UiPathComponent.prototype._preventClickJacking = function () {
        if (window.top !== window.self) {
            window.top.location.replace(window.self.location.href);
        }
    };
    UiPathComponent.prototype._showNotification = function (notification) {
        this._snack.show(notification.data.message, {
            type: SEVERITY_SNACKBAR_MAP.get(notification.severity),
        });
    };
    return UiPathComponent;
}());
export { UiPathComponent };
